(() => {
  const contextElement = document.getElementById('LinovaAnalyticsContext');
  if (!contextElement) return;

  let context = {};
  try {
    context = JSON.parse(contextElement.textContent || '{}');
  } catch {
    return;
  }

  document.documentElement.dataset.linovaAnalytics = 'ready';

  const recentEvents = new Map();

  const priceFromCents = (value) => {
    const cents = Number(value);
    return Number.isFinite(cents) ? cents / 100 : undefined;
  };

  const cleanItem = (item = {}) => {
    const cleaned = {
      item_id: item.item_id ? String(item.item_id) : undefined,
      item_variant: item.item_variant ? String(item.item_variant) : undefined,
      item_name: item.item_name || undefined,
      item_brand: item.item_brand || undefined,
      item_category: item.item_category || undefined,
      price: priceFromCents(item.price_cents),
      quantity: Number(item.quantity) || 1,
    };

    return Object.fromEntries(Object.entries(cleaned).filter(([, value]) => value !== undefined && value !== ''));
  };

  const itemFromElement = (element) => {
    if (!(element instanceof HTMLElement)) return {};

    return cleanItem({
      item_id: element.dataset.productId,
      item_variant: element.dataset.productVariantId,
      item_name: element.dataset.productTitle,
      item_brand: element.dataset.productVendor,
      item_category: element.dataset.productType,
      price_cents: element.dataset.productPriceCents,
      quantity: 1,
    });
  };

  const emit = (eventName, eventData = {}, dedupeKey = '') => {
    const now = Date.now();
    if (dedupeKey) {
      const previous = recentEvents.get(dedupeKey) || 0;
      if (now - previous < 1500) return;
      recentEvents.set(dedupeKey, now);
    }

    const payload = {
      event: eventName,
      ...eventData,
      linova: {
        page_type: context.page_type || '',
        path: context.path || window.location.pathname,
        source: 'shopify_theme',
      },
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    if (typeof window.gtag === 'function') {
      const ga4Data = eventData.ecommerce
        ? { ...eventData, ...eventData.ecommerce }
        : { ...eventData };
      delete ga4Data.ecommerce;
      window.gtag('event', eventName, ga4Data);
    }

    document.documentElement.dataset.linovaLastEvent = eventName;
    sessionStorage.setItem('linova:last_event', eventName);
    window.dispatchEvent(new CustomEvent('linova:analytics', { detail: payload }));
  };

  const ecommercePayload = (items, value) => {
    const payload = {
      currency: context.currency || 'USD',
      items,
    };

    if (Number.isFinite(value)) payload.value = value;
    return { ecommerce: payload };
  };

  const cartItems = () => (context.cart?.items || []).map(cleanItem);

  if (context.product) {
    const item = cleanItem(context.product);
    emit('view_item', ecommercePayload([item], item.price), `view_item:${item.item_id}:${item.item_variant}`);
  }

  if (context.page_type === 'search' && context.search_term) {
    emit('search', { search_term: context.search_term }, `search:${context.search_term}`);
  }

  if (document.querySelector('[data-linova-newsletter-success]')) {
    const newsletterKey = `linova-newsletter:${window.location.pathname}:${window.location.search}`;
    if (!sessionStorage.getItem(newsletterKey)) {
      sessionStorage.setItem(newsletterKey, '1');
      emit('newsletter_signup', { method: 'email' }, newsletterKey);
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const productLink = target.closest('[data-linova-product-link]');
      if (productLink instanceof HTMLElement) {
        const item = itemFromElement(productLink);
        const list = context.collection || {};
        emit(
          'select_item',
          {
            ecommerce: {
              item_list_id: list.item_list_id ? String(list.item_list_id) : undefined,
              item_list_name: list.item_list_name || undefined,
              items: [item],
            },
          },
          `select_item:${item.item_id}:${item.item_variant}`
        );
      }

      const addButton = target.closest('[data-linova-add-button]');
      if (addButton instanceof HTMLElement) {
        const item = itemFromElement(addButton);
        emit('add_to_cart', ecommercePayload([item], item.price), `add_to_cart:${item.item_id}:${item.item_variant}`);
      }

      const whatsappLink = target.closest('a[href*="wa.me/"], a[href*="api.whatsapp.com/"]');
      if (whatsappLink instanceof HTMLAnchorElement) {
        emit(
          'whatsapp_click',
          {
            link_url: whatsappLink.href,
            link_text: whatsappLink.textContent?.trim() || '',
            product_id: context.product?.item_id ? String(context.product.item_id) : undefined,
          },
          `whatsapp_click:${window.location.pathname}`
        );
      }

      const checkoutControl = target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
      if (checkoutControl) {
        emit(
          'begin_checkout',
          ecommercePayload(cartItems(), priceFromCents(context.cart?.value_cents)),
          `begin_checkout:${context.cart?.item_count || 0}`
        );
      }
    },
    { capture: true }
  );

  document.addEventListener('cart:update', (event) => {
    const data = event.detail?.data || {};
    if (data.didError || data.source !== 'product-form-component') return;

    const source = event.target instanceof Element ? event.target : null;
    const productContainer = source?.closest('product-card, [data-product-page]');
    let itemSource = productContainer?.querySelector('[data-linova-product-link], [data-linova-add-button]');

    if (!(itemSource instanceof HTMLElement) && data.productId) {
      itemSource = document.querySelector(`[data-product-id="${CSS.escape(String(data.productId))}"]`);
    }

    const item = itemSource instanceof HTMLElement ? itemFromElement(itemSource) : cleanItem({
      item_id: data.productId,
      item_variant: data.variantId,
      quantity: data.itemCount,
    });

    item.quantity = Number(data.itemCount) || 1;
    emit('add_to_cart', ecommercePayload([item], item.price), `add_to_cart:${item.item_id}:${item.item_variant}`);
  });

  document.addEventListener('filter:update', (event) => {
    const entries = event.detail?.queryParams ? Array.from(event.detail.queryParams.entries()) : [];
    const filters = entries.filter(([key, value]) => key.startsWith('filter.') && value !== '');
    if (filters.length === 0) return;

    emit(
      'filter_used',
      {
        filters: filters.map(([name, value]) => ({ name, value })),
        item_list_id: context.collection?.item_list_id ? String(context.collection.item_list_id) : undefined,
        item_list_name: context.collection?.item_list_name || undefined,
      },
      `filter_used:${filters.map(([name, value]) => `${name}:${value}`).join('|')}`
    );
  });
})();
